/**
 * VoiceActivityDetector - Browser-side VAD using WebAudio
 * 
 * Uses AudioWorklet to analyze audio energy and detect speech start/end
 * events to drive speech recognition lifecycle.
 * Now uses MicManager for centralized microphone access.
 */

import micManager from './MicManager.js';

export class VoiceActivityDetector {
    constructor(options = {}) {
        this.options = {
            // VAD thresholds
            energyThreshold: options.energyThreshold || 0.01,
            silenceThreshold: options.silenceThreshold || 0.005,
            silenceDuration: options.silenceDuration || 1000, // ms
            minSpeechDuration: options.minSpeechDuration || 200, // ms
            
            // Audio processing
            sampleRate: options.sampleRate || 16000,
            bufferSize: options.bufferSize || 1024,
            smoothingFactor: options.smoothingFactor || 0.8,
            
            ...options
        };
        
        this.audioContext = null;
        this.audioWorklet = null;
        this.mediaStream = null;
        this.audioSource = null;
        this.processor = null;
        
        this.isActive = false;
        this.isListening = false;
        this.currentEnergy = 0;
        this.smoothedEnergy = 0;
        this.speechStartTime = null;
        this.silenceStartTime = null;
        this.lastSpeechTime = null;
        
        // Callbacks
        this.onSpeechStart = null;
        this.onSpeechEnd = null;
        this.onEnergyUpdate = null;
        this.onError = null;
        
        // Timeouts
        this.silenceTimeout = null;
        this.speechTimeout = null;
    }

    /**
     * Initialize the VAD system
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        // No-op: VAD now always uses MicManager's AudioContext and stream in start().
        return true;
    }

    /**
     * Create audio worklet processor
     */
    async createAudioWorklet() {
        // Deprecated path: worklet is loaded from /vad-processor.js in start().
        return true;
    }

    /**
     * Start voice activity detection
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        if (this.isActive) {
            console.log('🎤 VAD: Already active');
            return true;
        }

        try {
            // Acquire microphone through MicManager
            console.log('🎤 VAD: Acquiring microphone through MicManager...');
            this.mediaStream = await micManager.acquire({
                audio: {
                    sampleRate: this.options.sampleRate,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });

            // Get AudioContext from MicManager
            this.audioContext = micManager.getAudioContext();
            if (!this.audioContext) {
                throw new Error('AudioContext not available from MicManager');
            }

            // Validate MediaStream
            if (!this.mediaStream) {
                throw new Error('MediaStream not available from MicManager');
            }

            // Create audio source using MicManager's validated stream
            this.audioSource = micManager.createMediaStreamSource();
            
            // Load AudioWorklet module
            console.log('🎤 VAD: Loading AudioWorklet module...');
            await this.audioContext.audioWorklet.addModule('/vad-processor.js');
            console.log('🎤 VAD: AudioWorklet module loaded');
            
            // Create worklet processor with threshold parameters
            const energyThreshold = this.options.energyThreshold || 0.005;
            const silenceThreshold = this.options.silenceThreshold || 0.002;
            
            // Validate parameters are finite numbers
            if (!isFinite(energyThreshold) || !isFinite(silenceThreshold)) {
                throw new Error(`Invalid VAD thresholds: energyThreshold=${energyThreshold}, silenceThreshold=${silenceThreshold}`);
            }
            
            this.processor = new AudioWorkletNode(this.audioContext, 'vad-processor', {
                parameterData: {
                    energyThreshold: energyThreshold,
                    silenceThreshold: silenceThreshold
                }
            });
            
            // Set up message handling
            this.processor.port.onmessage = (event) => {
                this.handleWorkletMessage(event.data);
            };
            
            // Connect audio pipeline
            this.audioSource.connect(this.processor);
            
            this.isActive = true;
            console.log('🎤 VAD: Started successfully');
            
            return true;
        } catch (error) {
            console.error('🎤 VAD: Failed to start:', error);
            if (this.onError) {
                this.onError(error);
            }
            return false;
        }
    }

    /**
     * Stop voice activity detection
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        if (!this.isActive) {
            return true;
        }

        try {
            // Clear timeouts
            this.clearTimeouts();
            
            // Disconnect audio pipeline
            if (this.audioSource) {
                this.audioSource.disconnect();
                this.audioSource = null;
            }
            
            if (this.processor) {
                this.processor.disconnect();
                this.processor = null;
            }
            
            // Release microphone through MicManager
            if (this.mediaStream) {
                micManager.release();
                this.mediaStream = null;
            }
            
            this.isActive = false;
            this.isListening = false;
            this.currentEnergy = 0;
            this.smoothedEnergy = 0;
            this.speechStartTime = null;
            this.silenceStartTime = null;
            this.lastSpeechTime = null;
            
            console.log('🎤 VAD: Stopped successfully');
            return true;
        } catch (error) {
            console.error('🎤 VAD: Failed to stop:', error);
            return false;
        }
    }

    /**
     * Handle messages from audio worklet
     * @param {Object} data - Message data
     */
    handleWorkletMessage(data) {
        const type = data.type;
        if (type === 'energy' || type === 'energy_update') {
            this.currentEnergy = data.energy;
            this.smoothedEnergy = data.smoothedEnergy;
            this.lastSpeechTime = Date.now();
            if (this.onEnergyUpdate) {
                this.onEnergyUpdate({
                    energy: this.currentEnergy,
                    smoothedEnergy: this.smoothedEnergy,
                    timestamp: data.timestamp
                });
            }
        } else if (type === 'speech_start') {
            this.handleSpeechStart(data);
        } else if (type === 'speech_end') {
            this.handleSpeechEnd(data);
        }
    }

    /**
     * Handle speech start detection
     */
    handleSpeechStart(data = {}) {
        const now = data.timestamp || Date.now();
        
        // Check minimum speech duration
        if (this.speechStartTime && (now - this.speechStartTime) < this.options.minSpeechDuration) {
            return; // Too short, ignore
        }
        
        this.speechStartTime = now;
        this.isListening = true;
        this.clearTimeouts();
        
        console.log('🎤 VAD: Speech start detected');
        
        if (this.onSpeechStart) {
            this.onSpeechStart({
                timestamp: now,
                energy: data.energy || this.smoothedEnergy
            });
        }
    }

    /**
     * Handle silence start detection
     */
    handleSilenceStart() {
        if (!this.isListening) return;
        
        const now = Date.now();
        
        if (!this.silenceStartTime) {
            this.silenceStartTime = now;
        }
        
        // Check if silence has persisted long enough
        const silenceDuration = now - this.silenceStartTime;
        
        if (silenceDuration >= this.options.silenceDuration) {
            this.handleSpeechEnd();
        } else {
            // Set timeout to check again
            this.silenceTimeout = setTimeout(() => {
                this.handleSpeechEnd();
            }, this.options.silenceDuration - silenceDuration);
        }
    }

    /**
     * Handle speech end detection
     */
    handleSpeechEnd(data = {}) {
        if (!this.isListening) return;
        
        const now = data.timestamp || Date.now();
        const speechDuration = data.duration || (this.speechStartTime ? now - this.speechStartTime : 0);
        
        this.isListening = false;
        this.speechStartTime = null;
        this.silenceStartTime = null;
        this.clearTimeouts();
        
        console.log('🎤 VAD: Speech end detected');
        
        if (this.onSpeechEnd) {
            this.onSpeechEnd({
                timestamp: now,
                duration: speechDuration
            });
        }
    }

    /**
     * Clear all timeouts
     */
    clearTimeouts() {
        if (this.silenceTimeout) {
            clearTimeout(this.silenceTimeout);
            this.silenceTimeout = null;
        }
        
        if (this.speechTimeout) {
            clearTimeout(this.speechTimeout);
            this.speechTimeout = null;
        }
    }

    /**
     * Update VAD thresholds
     * @param {Object} thresholds - New threshold values
     */
    updateThresholds(thresholds) {
        if (thresholds.energyThreshold !== undefined) {
            this.options.energyThreshold = thresholds.energyThreshold;
        }
        
        if (thresholds.silenceThreshold !== undefined) {
            this.options.silenceThreshold = thresholds.silenceThreshold;
        }
        
        if (thresholds.silenceDuration !== undefined) {
            this.options.silenceDuration = thresholds.silenceDuration;
        }
        
        if (thresholds.minSpeechDuration !== undefined) {
            this.options.minSpeechDuration = thresholds.minSpeechDuration;
        }
        
        console.log('🎤 VAD: Thresholds updated:', this.options);
        // Forward to worklet at runtime if available
        if (this.processor && this.processor.port) {
            try {
                this.processor.port.postMessage({
                    type: 'update_params',
                    energyThreshold: this.options.energyThreshold,
                    silenceThreshold: this.options.silenceThreshold,
                    silenceMs: this.options.silenceDuration,
                    minSpeechMs: this.options.minSpeechDuration
                });
            } catch (e) {
                // ignore
            }
        }
    }

    /**
     * Update parameters using alternate names and forward to worklet
     * @param {Object} params
     */
    updateParams(params = {}) {
        const mapped = { ...params };
        if (mapped.threshold !== undefined) this.options.energyThreshold = mapped.threshold;
        if (mapped.energyThreshold !== undefined) this.options.energyThreshold = mapped.energyThreshold;
        if (mapped.silenceMs !== undefined) this.options.silenceDuration = mapped.silenceMs;
        if (mapped.minSpeechMs !== undefined) this.options.minSpeechDuration = mapped.minSpeechMs;
        if (mapped.silenceThreshold !== undefined) this.options.silenceThreshold = mapped.silenceThreshold;
        // Reuse updateThresholds to set and propagate
        this.updateThresholds({
            energyThreshold: this.options.energyThreshold,
            silenceThreshold: this.options.silenceThreshold,
            silenceDuration: this.options.silenceDuration,
            minSpeechDuration: this.options.minSpeechDuration
        });
    }

    /**
     * Get current VAD state
     * @returns {Object} Current state
     */
    getState() {
        return {
            isActive: this.isActive,
            isListening: this.isListening,
            currentEnergy: this.currentEnergy,
            smoothedEnergy: this.smoothedEnergy,
            speechStartTime: this.speechStartTime,
            silenceStartTime: this.silenceStartTime,
            lastSpeechTime: this.lastSpeechTime,
            thresholds: { ...this.options }
        };
    }

    /**
     * Set speech start callback
     * @param {Function} callback - Callback function
     */
    setOnSpeechStart(callback) {
        this.onSpeechStart = callback;
    }

    /**
     * Set speech end callback
     * @param {Function} callback - Callback function
     */
    setOnSpeechEnd(callback) {
        this.onSpeechEnd = callback;
    }

    /**
     * Set energy update callback
     * @param {Function} callback - Callback function
     */
    setOnEnergyUpdate(callback) {
        this.onEnergyUpdate = callback;
    }

    /**
     * Set error callback
     * @param {Function} callback - Callback function
     */
    setOnError(callback) {
        this.onError = callback;
    }

    /**
     * Check if VAD is supported
     * @returns {boolean} Support status
     */
    static isSupported() {
        return !!(window.AudioContext || window.webkitAudioContext) &&
               window.AudioWorkletNode &&
               navigator.mediaDevices &&
               navigator.mediaDevices.getUserMedia;
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        
        // Force release microphone if still acquired
        if (this.mediaStream) {
            micManager.release(true);
            this.mediaStream = null;
        }
        
        this.onSpeechStart = null;
        this.onSpeechEnd = null;
        this.onEnergyUpdate = null;
        this.onError = null;
    }
}
